import graphSnapshot from '../data/graph-snapshot.json';
import productServiceMapping from '../data/product-service-mapping.json';

/**
 * Maps a calculator UI deployment type key to the baseline distribution identifier.
 */
export function getDistribution(deploymentType: string): string {
  switch (deploymentType) {
    case 'eval':
    case 'lite':
    case 'ha':
      return 'rke2';
    case 'air-aks':
      return 'aks';
    case 'air-eks':
      return 'eks';
    case 'air-openshift':
      return 'openshift';
    default:
      return 'rke2';
  }
}

/**
 * Maps a calculator deployment type + high-level parameters to the baseline profile key
 * (default, lite, ha).
 */
export function getBaselineProfile(
  deploymentType: string,
  highLevelParameters?: HighLevelParametersInterface
): string {
  switch (deploymentType) {
    case 'eval':
      return 'default';
    case 'lite':
      return 'lite';
    case 'ha':
      return 'ha';
    case 'air-aks': {
      const sub = highLevelParameters?.aksProfile || 'default';
      return sub === 'ha' ? 'ha' : sub === 'lite' ? 'lite' : 'default';
    }
    case 'air-eks': {
      const sub = highLevelParameters?.eksProfile || 'default';
      return sub === 'ha' ? 'ha' : sub === 'lite' ? 'lite' : 'default';
    }
    case 'air-openshift': {
      const sub = highLevelParameters?.openshiftProfile || 'default';
      return sub === 'ha' ? 'ha' : sub === 'lite' ? 'lite' : 'default';
    }
    default:
      return 'default';
  }
}

/**
 * Returns the set of product keys available for a given distribution,
 * based on graph-snapshot profiles and product-service-mapping.
 * Excludes fabric (not user-selectable).
 */
export function getAvailableProducts(distribution: string): Set<string> {
  const profiles = (graphSnapshot as any).profiles[distribution];
  if (!profiles) return new Set();

  // Build set of all service names in this distribution
  const distServices = new Set<string>();
  for (const entry of profiles) {
    distServices.add(entry.name);
  }

  // Build reverse lookup: service name → product key
  const serviceToProduct = new Map<string, string>();
  const products = (productServiceMapping as any).products;
  for (const [productKey, productDef] of Object.entries(products)) {
    const def = productDef as { type: string; services: { name: string; displayName: string }[] };
    if (def.type === 'fabric') continue; // Exclude fabric
    for (const svc of def.services) {
      serviceToProduct.set(svc.name, productKey);
    }
  }

  // Find products that have at least one service in this distribution
  const available = new Set<string>();
  distServices.forEach((svcName) => {
    const productKey = serviceToProduct.get(svcName);
    if (productKey) {
      available.add(productKey);
    }
  });

  // DU Modern Projects is a virtual product (resources from overrides, no graph services)
  // Always available when DU AI Center is available
  if (available.has('document_understanding_ai_center')) {
    available.add('document_understanding_modern_projects');
  }

  return available;
}
